import {MC_2_0_PLANS, NEW_SEO_PLAN} from '../../../../utils/constants'
import {KEAP_FAMILY_CODES} from '../../../../features/CatalogGrid/utils/productSelectionRules'
import {buildSalesAttributionFields} from '../../../../features/CatalogGrid/utils/buildSendLinkSubscriptionGroups'

// Builds the request body for ordercapture/services/createOrder when the
// rep clicks Continue on Catalog Selection — reuses the SAME endpoint and
// status convention as the legacy new-order.js flow's 'catalog-continue-summary'
// context (see captureOrder5NewUpgrade in screens/new-order.js): the order is
// created with order_status 'Pending' and its lines populated from the cart,
// then later transitions (Submitted/Completed/Cancelled) belong to whatever
// consumes the order downstream — out of scope here.
//
// items: a PurchaseLinkSlice location's `items` array (same shape
// buildLocationOrderSummary in pages/CatalogSelection/utils/orderSummaryUtils
// already parses for the Order Summary panel).
// locationThryvId: the location's own thryvId (loc.thryvId), tagged onto
// every line so order_line.location_thryv_id is populated from order-create
// time already, same field buildSendLinkPayload tags its lines with —
// send-link's delete+reinsert still overwrites this
// column later, but tagging it here means it's already correct for any
// order that never reaches send-link (e.g. the Scheduling step's
// updateOrder round trip, which also deletes and reinserts order_line).
function buildLinesFromItems(
  items = [],
  startLineNumber = 1,
  locationThryvId = null,
) {
  const lines = []
  let lineNumber = startLineNumber

  items
    .filter(item => !item.alreadyIncluded)
    .forEach(item => {
      const isCenter = item.productType === 'CENTER'
      const isOneTimeFee = item.productType === 'ONE_TIME_FEE'
      const category = isCenter ? 'Center' : 'App'
      const parentLineNumber = lineNumber

      lines.push({
        line_number: lineNumber,
        parent_line_number: null,
        plansv5_id: null,
        action_code: 'New',
        order_line_status: 'InProgress',
        saas_product_category: category,
        saas_product_type: isOneTimeFee
          ? 'one_time_fee'
          : isCenter
          ? 'main'
          : 'addon',
        saas_product_code: item.productCode,
        saas_instance_id: null,
        // Schema field name is misspelled upstream (order-capture-schema.js) —
        // must match exactly or validation-middleware's stripUnknown drops it.
        recurly_subscirption_id: null,
        total_cost:
          Number(item.dueTodayAmount ?? item.unitPrice ?? 0) *
          (item.quantity ?? 1),
        unit_cost: Number(item.unitPrice ?? 0),
        quantity: item.quantity ?? 1,
        start_date: new Date().toISOString(),
        end_date: null,
        location_thryv_id: locationThryvId,
      })
      lineNumber += 1
      ;(item.addons ?? [])
        .filter(addon => !addon.alreadyIncluded)
        .forEach(addon => {
          lines.push({
            line_number: lineNumber,
            parent_line_number: parentLineNumber,
            plansv5_id: null,
            action_code: 'New',
            order_line_status: 'InProgress',
            saas_product_category: category,
            saas_product_type: isCenter ? 'entitlement' : 'addon',
            saas_product_code: addon.productCode,
            saas_instance_id: null,
            recurly_subscirption_id: null,
            total_cost:
              Number(addon.dueTodayAmount ?? addon.unitPrice ?? 0) *
              (addon.quantity ?? 1),
            unit_cost: Number(addon.unitPrice ?? 0),
            quantity: addon.quantity ?? 1,
            start_date: new Date().toISOString(),
            end_date: null,
            location_thryv_id: locationThryvId,
          })
          lineNumber += 1
        })
    })

  return lines
}

// See buildSendLinkPayload.js's readConfiguredOption/extractLocationDetail —
// same lifting logic, duplicated here in order-capture-schema's snake_case
// shape (location_thryv_id/mc_domain/...) since the two schemas don't share a
// field-naming convention.
// configuration vs configuredOptions: see buildSendLinkPayload.js's
// readConfiguredOption — productSelectionRules.js normalizes an item seeded
// from an existing Recurly subscription to `configuration` instead of
// `configuredOptions` (buildPaymentPreviewPayload.js's resolveQuantity/New
// Alpha SEO quantity resolution both check both for this reason).
function readConfiguredOption(item, key) {
  const configuredOptions = item?.configuration ?? item?.configuredOptions
  if (Array.isArray(configuredOptions)) {
    return configuredOptions.find(f => f.name === key)?.value ?? undefined
  }
  return configuredOptions?.[key] ?? undefined
}

function extractLocationDetailSnake(loc) {
  const items = loc.items ?? []
  const mcItem = items.find(i => MC_2_0_PLANS.includes(i.productCode))
  const keapItem = items.find(i => KEAP_FAMILY_CODES.includes(i.productCode))
  const seoItem = items.find(i => i.productCode === NEW_SEO_PLAN)
  return {
    location_thryv_id: loc.thryvId,
    mc_domain: readConfiguredOption(mcItem, 'domain'),
    keap_domain: readConfiguredOption(keapItem, 'domain'),
    keap_trail_id: readConfiguredOption(keapItem, 'keapFreeTrialAppId'),
    seo_proposal_id: readConfiguredOption(seoItem, 'proposalId'),
    // Appointments aren't booked yet at Catalog Selection Continue time
    // (Scheduling is a later step) — always undefined here, populated later
    // via the Scheduling step's updateOrder round trip.
    onboarding_appt_id: loc.onboardingApptId,
    thryv_success_services_appt_id: loc.thryvSuccessServicesApptId,
  }
}

// Which locations count toward "this order" — parentPay combines every
// parent-pay location into one order (the account-header combined flow);
// single-location mode scopes to just the active one. Shared with
// shouldRequireOnboardingCall's routing decision so both agree on what
// "the order" contains for a given orderMode.
export function getRelevantLocations({
  locations,
  orderMode,
  selectedLocationId,
}) {
  return orderMode === 'parentPay'
    ? locations
    : locations.filter(l => l.locationId === selectedLocationId)
}

// accountInfo: PurchaseLinkSlice.accountInfo
// locations: PurchaseLinkSlice.locations
// orderMode: PurchaseLinkSlice.orderMode ('location' | 'parentPay')
// selectedLocationId: PurchaseLinkSlice.selectedLocationId
// isUpgrade: PurchaseLinkSlice.isUpgrade — set by handleOrder/handleSaveAndProceed
// from the accounts/upgradeTarget v5upgrade check (AllAccounts/MyAccounts) when
// this journey started on an existing location, not inferred from whether the
// location already has an active subscription: every account, new or not, gets
// a real $0 baseline subscription (Thryv My Account Basic+) the instant it's
// created, so "has an active subscription" can't tell new from upgrade on its own.
export function buildCreateOrderPayload({
  accountInfo,
  locations,
  orderMode,
  selectedLocationId,
  isUpgrade = false,
  salesRepId = null,
  // The rep's role and the "Create Purchase Link" modal's sales/partner
  // attribution (PurchaseLinkSlice's userRole / salesAttributionFields).
  userRole = null,
  salesAttributionFields = null,
}) {
  // parentPay combines every parent-pay location into one order — the
  // order-capture schema has no per-line location field, so lines from all
  // locations are flattened together (a known simplification; see design
  // discussion — the schema predates multi-location purchase links).
  const relevantLocations = getRelevantLocations({
    locations,
    orderMode,
    selectedLocationId,
  })

  // A combined parentPay order bills to the parent account, so its identity/
  // address is what order_header should carry. A single-location order bills
  // to (and must identify as) that one location, not the parent it happens
  // to hang off of — see PurchaseLinkSlice's locations[].accountInfo, set by
  // buildPurchaseLinkState from that location's own record. Mirrors
  // buildSendLinkPayload's identical `identity` selection.
  const identity =
    orderMode === 'parentPay'
      ? accountInfo
      : relevantLocations[0]?.accountInfo ?? accountInfo

  const lines = relevantLocations.reduce((acc, loc) => {
    return [
      ...acc,
      ...buildLinesFromItems(loc.items, acc.length + 1, loc.thryvId),
    ]
  }, [])

  // Same resolver buildSendLinkPayload uses: behalf* only for 'sales-rep',
  // xpCode only for the partner toggles, override* only when overrideSST is 'yes'.
  // processPurchase later feeds order_header.xp_code / on_behalf_of /
  // sales_rep_or_partner into rep-attribution-matrix, so they must be stored here
  // — the account's own identity.xpCode is not the rep's selection (and for a
  // combined parentPay order `identity` is the parent, which never carries it).
  const attribution = buildSalesAttributionFields(salesAttributionFields)
  const salesRepOrPartner = attribution.salesRepOrPartner || null

  return {
    order_status: 'Pending',
    order_version: '5.0',
    order_flow: isUpgrade ? 'Update' : 'New',
    order_completed_date: null,
    country_code: identity?.country || 'US',
    account_email: identity?.email || null,
    contact_first_name: identity?.firstName || null,
    contact_last_name: identity?.lastName || null,
    contact_phone: identity?.phone || null,
    contact_mobile: identity?.mobile || null,
    contact_email: identity?.email || null,
    company_name: identity?.businessName || null,
    // order_header's business-address columns — the payment page reads these
    // back (via businessAddress in cc/order/purchase-plan) so the "Same as my
    // business address" checkbox on the payment form has something to copy.
    company_address1: identity?.address || null,
    company_city: identity?.city || null,
    company_state: identity?.state || null,
    company_zipcode: identity?.postCode || null,
    company_phone: identity?.phone || null,
    thryv_id: identity?.thryvId || null,
    account_code: null,
    eaid: null,
    sales_rep_id: salesRepId || null,
    // The backend's rep-attribution matrix reads this network ID as the
    // selected Sales Rep and writes it to Recurly's On_Behalf_Of field.
    // Keep the email separately in behalf_email for notification recipients.
    on_behalf_of: attribution.behalfRepId || null,
    // Fall back to the account's own xpCode only when the rep made no
    // attribution choice at all (e.g. the direct-upgrade shortcut).
    xp_code:
      attribution.xpCode ||
      (salesRepOrPartner ? null : identity?.xpCode) ||
      null,
    rep_role: userRole || null,
    sales_rep_or_partner: salesRepOrPartner,
    override_rep_id: attribution.overrideRepId || null,
    behalf_email: attribution.behalfEmail || null,
    behalf_first_name: attribution.behalfFirstName || null,
    behalf_last_name: attribution.behalfLastName || null,
    lines,
    // One entry per location — see extractLocationDetailSnake. Populates
    // order_location_detail alongside order_line so a combined multi-location
    // order's mc/keap domain and seo proposal id are already correct from
    // order-create time (appointment ids follow later, from the Scheduling
    // step's updateOrder call).
    location_details: relevantLocations
      .map(extractLocationDetailSnake)
      .filter(d => Boolean(d.location_thryv_id)),
    discounts: [],
    reseller: [],
  }
}
